import { useState } from "react";
import { CloudUpload, Download, Upload, Trash2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { downloadFile } from "@/lib/utils/downloadFile";
import { useFileUpload } from "./useFileUpload";
import { useTran } from "@/hooks";

const DEFAULT_ACCEPT = ".pdf,.csv,.xlsx,.xls,.png,.jpg,.jpeg,.webp,.gif";

// `accept` entries come in two shapes across consumers of this shared
// component: extensions (".pdf") or MIME types ("image/png"). Normalize
// both to a short readable token — "PDF", "PNG" — rather than assuming one
// shape and rendering the other's raw value (e.g. "IMAGE/PNG") verbatim.
const formatAcceptedTypes = (accept) => {
  const extensions = [
    ...new Set(
      accept
        .split(",")
        .map((entry) => entry.trim())
        .filter(Boolean)
        .map((entry) =>
          entry.startsWith(".")
            ? entry.slice(1)
            : (entry.split("/")[1] || entry).split("+")[0],
        )
        .map((entry) => entry.toUpperCase())
        .filter(Boolean),
    ),
  ];
  if (extensions.length === 0) return "";
  if (extensions.length === 1) return extensions[0];
  return `${extensions.slice(0, -1).join(", ")} or ${extensions[extensions.length - 1]}`;
};

// Shared by both the image-preview and generic-file states below, so an
// already-uploaded file always offers the same three actions in the same
// style regardless of its type.
const FileActions = ({
  effectiveUrl,
  displayName,
  onBrowseClick,
  onRemove,
  disabled,
  t,
}) => (
  <div className="flex shrink-0 flex-col items-stretch gap-2">
    <Button type="button" size="sm" onClick={onBrowseClick} disabled={disabled}>
      <Upload className="h-3.5 w-3.5" />
      {t("replace")}
    </Button>
    {effectiveUrl && (
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={disabled}
        onClick={() => downloadFile(effectiveUrl, displayName)}
      >
        <Download className="h-3.5 w-3.5" />
        {t("download")}
      </Button>
    )}
    <Button
      type="button"
      size="sm"
      variant="outline"
      onClick={onRemove}
      disabled={disabled}
      className="text-destructive hover:text-destructive"
    >
      <Trash2 className="h-3.5 w-3.5" />
      {t("remove")}
    </Button>
  </div>
);

const FileUploadDropzone = ({
  label,
  value,
  onChange,
  accept = DEFAULT_ACCEPT,
  baseUrl = "",
  compact = false,
  className,
  labelClassName,
  frameClassName,
  disabled = false,
  attachedText,
}) => {
  const { t } = useTran();
  const {
    inputRef,
    effectiveUrl,
    displayName,
    isImage,
    hasAnyFile,
    handleFile,
    onBrowseClick,
  } = useFileUpload({ value, onChange, baseUrl });
  const [isDragOver, setIsDragOver] = useState(false);
  const acceptedTypesLabel = formatAcceptedTypes(accept);
  const resolvedAttachedText = attachedText ?? t("file_attached");

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragOver(false);
    if (disabled) return;
    const file = event.dataTransfer.files?.[0] ?? null;
    if (file) handleFile(file);
  };

  return (
    <div className={cn("space-y-2", className)}>
      {label && (
        <Label className={cn(compact ? "text-sm" : "text-xs", labelClassName)}>
          {label}
        </Label>
      )}

      <div
        className={cn(
          "relative flex flex-col items-center justify-center rounded-lg border-2 border-dashed text-center transition-colors",
          compact ? "min-h-0 gap-1.5 px-1 py-2" : "min-h-[11rem] gap-3 p-6",
          isDragOver ? "border-primary bg-primary/5" : "border-border",
          !hasAnyFile && "bg-muted/20",
          frameClassName,
        )}
        onDragOver={(event) => {
          event.preventDefault();
          if (!disabled) setIsDragOver(true);
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={handleDrop}
      >
        {hasAnyFile && compact ? (
          // Original vertical layout, kept for consumers like the
          // custom-framework logo picker that render this inside a narrow
          // fixed-width column (FrameworkFormDialog's w-40) — the newer
          // horizontal layout below doesn't fit that width and object-cover
          // crops a non-square logo. object-contain here shows it in full.
          isImage && effectiveUrl ? (
            <>
              <img
                src={effectiveUrl}
                alt={displayName || t("preview")}
                className="max-h-40 w-full rounded-md object-contain"
              />
              <div className="absolute right-2 top-2 flex gap-1">
                <button
                  type="button"
                  onClick={onBrowseClick}
                  title={t("replace")}
                  disabled={disabled}
                  className="rounded-md bg-background/90 p-1.5 shadow hover:bg-muted"
                >
                  <Upload className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => onChange?.(null)}
                  title={t("remove")}
                  disabled={disabled}
                  className="rounded-md bg-background/90 p-1.5 text-destructive shadow hover:bg-muted"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="max-w-full truncate text-sm font-medium text-foreground">
                {displayName || t("file")}
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  onClick={onBrowseClick}
                  disabled={disabled}
                >
                  {t("replace")}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => onChange?.(null)}
                  disabled={disabled}
                >
                  {t("remove")}
                </Button>
              </div>
            </>
          )
        ) : hasAnyFile ? (
          <div className="flex w-full items-center justify-between gap-4">
            <div className="flex min-w-0 flex-1 items-center gap-3">
              {isImage && effectiveUrl && (
                <img
                  src={effectiveUrl}
                  alt={displayName || t("preview")}
                  className="h-24 w-24 shrink-0 rounded-md border border-border object-cover"
                />
              )}
              <div className="min-w-0 text-left">
                <p className="truncate text-sm font-medium text-foreground">
                  {displayName || t("file")}
                </p>
                <p className="text-xs text-muted-foreground">
                  {resolvedAttachedText}
                </p>
              </div>
            </div>
            <FileActions
              effectiveUrl={effectiveUrl}
              displayName={displayName}
              onBrowseClick={onBrowseClick}
              onRemove={() => onChange?.(null)}
              disabled={disabled}
              t={t}
            />
          </div>
        ) : (
          <>
            <div
              className={cn(
                "flex items-center justify-center rounded-full bg-primary/10 text-primary",
                compact ? "h-8 w-8" : "h-12 w-12",
              )}
            >
              <CloudUpload className={compact ? "h-4 w-4" : "h-6 w-6"} />
            </div>
            <p
              className={cn(
                "text-muted-foreground",
                compact ? "text-xs leading-tight" : "text-sm",
              )}
            >
              {t("drag_drop_files")}
            </p>
            <p
              className={cn(
                "font-medium text-muted-foreground",
                compact ? "text-[10px] leading-none" : "text-xs",
              )}
            >
              {t("or")}
            </p>
            <Button
              type="button"
              size={compact ? "xs" : "sm"}
              onClick={onBrowseClick}
              disabled={disabled}
            >
              {t("browse_files")}
            </Button>
            {acceptedTypesLabel && (
              <p
                className={cn(
                  "text-muted-foreground",
                  compact ? "text-[10px] leading-tight" : "text-xs",
                )}
              >
                {acceptedTypesLabel}
              </p>
            )}
          </>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        className="hidden"
        disabled={disabled}
        accept={accept}
        onChange={(event) => {
          const file =
            event.target.files && event.target.files[0]
              ? event.target.files[0]
              : null;
          if (file) handleFile(file);
          if (inputRef.current) inputRef.current.value = "";
        }}
      />
    </div>
  );
};

export default FileUploadDropzone;
